/**
 * B-653 — znacznik kontroli trzeźwości na listach i w widokach dokumentu (kontrola trzeźwości, etap E1).
 *
 * ⚠️ LUSTRO: `pwa/src/lib/znacznikTrzezwosci.ts` — identyczne poza ścieżką lustra w tym wierszu.
 * Strażnik: `scripts/__tests__/znacznik-trzezwosci.test.mjs`.
 *
 * Dane: serwer liczy przy każdym zapisie dokumentu (`ocena-trzezwosci.ts`) dwie kolumny i oddaje je tylko roli, która
 * widzi dane o zdrowiu (D1, decyzja właściciela 21.10.2026 — sam znacznik zdradza wynik, bo pojawia się tylko przy
 * wyniku pozytywnym). Roli bez tej klasy obie przychodzą jako `null`, więc znacznika po prostu nie ma.
 *   • `sobrietyFlag` — najcięższy wynik w dokumencie: POSITIVE (pracownik GAiT, pomiar pozytywny) > REFUSED (pracownik
 *     GAiT odmówił badania) > FOREIGN (uczestnik obcy w kolizji, pozytywny) > NEGATIVE;
 *   • `sobrietyAt` — godzina badania pierwszego pomiaru o tym wyniku, `YYYY-MM-DDTHH:mm` (czas lokalny), albo `null`
 *     (pomiar sprzed godziny badania — „godz. nieznana”, bez zgadywania z godziny wysyłki, D9).
 *
 * Negatywny nie dostaje znacznika na listach (czerwień niesie jedno znaczenie) — tylko w widoku dokumentu i rejestrze,
 * gdzie wywołujący prosi o niego jawnie (`pokazNegatywny`).
 */

export type FlagaTrzezwosci = 'POSITIVE' | 'REFUSED' | 'FOREIGN' | 'NEGATIVE';

/** `czerwony` — wypełniony czerwony; `obrys` — czerwony obrys; `fiolet` — uczestnik obcy; `zielony` — negatywny. */
export type TonZnacznikaTrzezwosci = 'czerwony' | 'obrys' | 'fiolet' | 'zielony';

export interface ZnacznikTrzezwosci {
  /** Krótki napis na znaczniku, np. „pozytywny · 07:42”. */
  tekst: string;
  ton: TonZnacznikaTrzezwosci;
  /** Pełne zdanie do dymka / czytnika ekranu, np. „Pozytywny wynik badania trzeźwości, 21.10.2026, godz. 07:42”. */
  tytul: string;
}

const FLAGI: readonly FlagaTrzezwosci[] = ['POSITIVE', 'REFUSED', 'FOREIGN', 'NEGATIVE'];

/** Czy wartość z API jest znaną flagą (API przyjmuje i oddaje dowolny JSON — nieznana wartość = brak znacznika). */
export function jestFlagaTrzezwosci(x: unknown): x is FlagaTrzezwosci {
  return typeof x === 'string' && (FLAGI as readonly string[]).includes(x);
}

/** Rozbiór `YYYY-MM-DDTHH:mm` — `null`, gdy napis ma inny kształt albo niemożliwą godzinę. */
export function rozbierzGodzineBadania(x: unknown): { data: string; godzina: string } | null {
  if (typeof x !== 'string') return null;
  const m = x.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, r, mies, d, h, min] = m;
  if (Number(h) > 23 || Number(min) > 59 || Number(mies) < 1 || Number(mies) > 12 || Number(d) < 1 || Number(d) > 31) return null;
  return { data: `${d}.${mies}.${r}`, godzina: `${h}:${min}` };
}

const OPIS: Record<FlagaTrzezwosci, { krotko: string; zdanie: string; ton: TonZnacznikaTrzezwosci }> = {
  POSITIVE: { krotko: 'pozytywny', zdanie: 'Pozytywny wynik badania trzeźwości', ton: 'czerwony' },
  REFUSED: { krotko: 'odmowa', zdanie: 'Odmowa poddania się badaniu trzeźwości', ton: 'obrys' },
  FOREIGN: { krotko: 'obcy · pozytywny', zdanie: 'Pozytywny wynik badania trzeźwości uczestnika spoza GAiT', ton: 'fiolet' },
  NEGATIVE: { krotko: 'negatywny', zdanie: 'Negatywny wynik badania trzeźwości', ton: 'zielony' },
};

/**
 * Znacznik dla wiersza listy albo nagłówka dokumentu — `null`, gdy znacznika nie ma (brak flagi, flaga nieznana,
 * negatywny bez `pokazNegatywny`).
 */
export function znacznikTrzezwosci(
  flaga: unknown,
  godzinaBadania: unknown,
  opcje?: { pokazNegatywny?: boolean },
): ZnacznikTrzezwosci | null {
  if (!jestFlagaTrzezwosci(flaga)) return null;
  if (flaga === 'NEGATIVE' && !opcje?.pokazNegatywny) return null;
  const o = OPIS[flaga];
  const g = rozbierzGodzineBadania(godzinaBadania);
  return {
    tekst: g ? `${o.krotko} · ${g.godzina}` : o.krotko,
    ton: o.ton,
    tytul: g ? `${o.zdanie}, ${g.data}, godz. ${g.godzina}` : `${o.zdanie}, godz. nieznana`,
  };
}
